// =====================================================================
// EncounterList (PME Sprint 2 / S51) — conteúdo da aba "Consultas" do paciente.
// Mostra: (1) timeline de consultas (SOAP) com data/tipo/profissional/status,
// (2) histórico na agenda médica (clinic_appointments, read-only) + "Nova consulta".
// =====================================================================
import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import { makeStyles } from "@material-ui/core/styles";
import {
  Paper,
  Typography,
  Button,
  Chip,
  Divider,
  CircularProgress,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import EventNoteIcon from "@material-ui/icons/EventNote";
import DescriptionIcon from "@material-ui/icons/Description";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";

import {
  listEncounters,
  listPatientAppointments,
  listEncounterProfessionals,
  deleteEncounter,
  encounterErrorMessage,
  ENCOUNTER_TYPE_LABELS,
  ENCOUNTER_STATUS_LABELS
} from "../../services/encounterService";
import useIsMobile from "../../utils/useIsMobile";

const useStyles = makeStyles((theme) => ({
  headerRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: theme.spacing(1.5),
    flexWrap: "wrap",
    gap: theme.spacing(1)
  },
  sectionTitle: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    fontWeight: 600
  },
  row: {
    padding: theme.spacing(1.5, 2),
    marginBottom: theme.spacing(1),
    cursor: "pointer",
    transition: "background 0.15s",
    "&:hover": { background: theme.palette.action.hover }
  },
  apptRow: {
    padding: theme.spacing(1.25, 2),
    marginBottom: theme.spacing(1)
  },
  rowTop: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    flexWrap: "wrap"
  },
  meta: {
    color: theme.palette.text.secondary,
    fontSize: 13
  },
  detailGrid: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(0.5, 2),
    marginTop: 4,
    fontSize: 13,
    color: theme.palette.text.secondary
  },
  detailItem: {
    marginRight: theme.spacing(2)
  },
  agendaHint: {
    color: theme.palette.text.secondary,
    fontSize: 12.5,
    marginBottom: theme.spacing(1.5)
  },
  empty: {
    color: theme.palette.text.secondary,
    fontStyle: "italic",
    padding: theme.spacing(2)
  },
  block: {
    marginBottom: theme.spacing(3)
  }
}));

const pad = (n) => String(n).padStart(2, "0");
const fmtDateTime = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const statusChip = (status) => {
  const label = ENCOUNTER_STATUS_LABELS[status] || status || "—";
  if (status === "signed") return <Chip size="small" label={label} style={{ background: "#d1fae5", color: "#059669" }} />;
  if (status === "open") return <Chip size="small" label={label} color="primary" variant="outlined" />;
  return <Chip size="small" label={label} />;
};

const apptStatusLabel = {
  scheduled: "Agendado",
  confirmed: "Confirmado",
  completed: "Realizado",
  cancelled: "Cancelado",
  no_show: "Não compareceu"
};

const EncounterList = ({ patientId }) => {
  const classes = useStyles();
  const history = useHistory();
  const isMobile = useIsMobile();

  const [encounters, setEncounters] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [profMap, setProfMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [confirmDel, setConfirmDel] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      try {
        const [encRes, profRes] = await Promise.all([
          listEncounters(patientId),
          listEncounterProfessionals()
        ]);
        // agenda é best-effort (não bloqueia a aba)
        let appts = [];
        try {
          const ar = await listPatientAppointments(patientId);
          appts = (ar && ar.appointments) || [];
        } catch (e) {
          /* ignora */
        }
        if (!active) return;
        const map = {};
        ((profRes && profRes.professionals) || []).forEach((p) => {
          map[String(p.id)] = p.name;
        });
        setProfMap(map);
        setEncounters((encRes && encRes.encounters) || []);
        setAppointments(appts);
      } catch (err) {
        if (active) toast.error(encounterErrorMessage(err));
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [patientId]);

  const handleDelete = async () => {
    if (!confirmDel) return;
    setDeleting(true);
    try {
      await deleteEncounter(confirmDel.id);
      setEncounters((prev) => prev.filter((x) => x.id !== confirmDel.id));
      toast.success("Consulta excluída.");
      setConfirmDel(null);
    } catch (err) {
      toast.error(encounterErrorMessage(err));
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", padding: 48 }}>
        <CircularProgress />
      </div>
    );
  }

  return (
    <div style={{ padding: 4 }}>
      <div className={classes.headerRow}>
        <Typography variant="h6" className={classes.sectionTitle}>
          <DescriptionIcon fontSize="small" /> Consultas ({encounters.length})
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => history.push(`/prontuario/paciente/${patientId}/consulta/nova`)}
        >
          Nova consulta
        </Button>
      </div>

      {/* ---------- Consultas (SOAP) ---------- */}
      <div className={classes.block}>
        {encounters.length === 0 ? (
          <Paper variant="outlined" className={classes.empty} component="div">
            Nenhuma consulta registrada ainda. Clique em <strong>Nova consulta</strong> para começar a evolução do paciente.
          </Paper>
        ) : (
          encounters.map((e) => (
            <Paper
              key={e.id}
              variant="outlined"
              className={classes.row}
              onClick={() => history.push(`/prontuario/consulta/${e.id}`)}
            >
              <div className={classes.rowTop}>
                <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
                  {fmtDateTime(e.encounterDate)}
                </Typography>
                <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                  {statusChip(e.status)}
                  {e.status === "open" && (
                    <Tooltip title="Excluir consulta (só em aberto)">
                      <IconButton
                        size="small"
                        onClick={(ev) => {
                          ev.stopPropagation();
                          setConfirmDel(e);
                        }}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </div>
              </div>
              <div className={classes.meta}>
                {ENCOUNTER_TYPE_LABELS[e.encounterType] || e.encounterType || "Consulta"}
                {e.professionalId && profMap[String(e.professionalId)] ? ` · ${profMap[String(e.professionalId)]}` : ""}
                {e.assessment ? ` · ${String(e.assessment).slice(0, 80)}${String(e.assessment).length > 80 ? "…" : ""}` : ""}
              </div>
            </Paper>
          ))
        )}
      </div>

      <Divider style={{ marginBottom: 16 }} />

      {/* ---------- Histórico na agenda (read-only, sincronizado) ---------- */}
      <div className={classes.headerRow}>
        <Typography variant="h6" className={classes.sectionTitle}>
          <EventNoteIcon fontSize="small" /> Histórico na agenda ({appointments.length})
        </Typography>
        <Button
          variant="outlined"
          size="small"
          startIcon={<OpenInNewIcon />}
          onClick={() => history.push("/agenda-medica")}
        >
          Abrir agenda médica
        </Button>
      </div>
      <Typography className={classes.agendaHint}>
        Agendar, remarcar e marcar retorno é feito na <strong>Agenda Médica</strong> (que sincroniza
        sozinha). Aqui o prontuário mostra os agendamentos do paciente em modo leitura.
      </Typography>
      {appointments.length === 0 ? (
        <Paper variant="outlined" className={classes.empty} component="div">
          Nenhum agendamento encontrado para este paciente na agenda médica (busca por telefone).
        </Paper>
      ) : (
        appointments.map((a) => (
          <Paper key={a.id} variant="outlined" className={classes.apptRow}>
            <div className={classes.rowTop}>
              <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
                {fmtDateTime(a.datetimeStart)}
                {a.durationMinutes ? ` · ${a.durationMinutes} min` : ""}
              </Typography>
              <Chip size="small" label={apptStatusLabel[a.status] || a.status || "—"} variant="outlined" />
            </div>
            <div className={classes.detailGrid}>
              <span><strong>Profissional:</strong> {a.professionalName || "—"}</span>
              <span><strong>Procedimento:</strong> {a.procedureType || "consulta"}</span>
              <span>
                <strong>Pagamento:</strong>{" "}
                {a.paymentType === "convenio"
                  ? `Convênio${a.insuranceName ? " · " + a.insuranceName : ""}`
                  : a.paymentType === "particular"
                  ? "Particular"
                  : a.paymentType || "—"}
              </span>
              {a.valorTotal ? (
                <span><strong>Valor:</strong> R$ {Number(a.valorTotal).toFixed(2)}</span>
              ) : null}
              {a.isTeleconsulta ? <span>🖥️ Teleconsulta</span> : null}
              {a.isReturn ? <span>↩️ Retorno</span> : null}
            </div>
          </Paper>
        ))
      )}

      {/* Confirmar exclusão de consulta (só em aberto) */}
      <Dialog open={!!confirmDel} onClose={() => !deleting && setConfirmDel(null)} maxWidth="xs" fullWidth fullScreen={isMobile}>
        <DialogTitle>Excluir consulta?</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2">
            A consulta de <strong>{confirmDel ? fmtDateTime(confirmDel.encounterDate) : ""}</strong> (em aberto)
            será removida do prontuário. Só consultas <strong>não assinadas</strong> podem ser excluídas.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDel(null)} disabled={deleting}>Cancelar</Button>
          <Button
            onClick={handleDelete}
            disabled={deleting}
            style={{ color: "#c0392b" }}
            startIcon={deleting ? <CircularProgress size={16} /> : <DeleteOutlineIcon />}
          >
            Excluir
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default EncounterList;
